import { useEffect, useState } from "react";
import { CheckCircle, Loader2, Package, Home } from "lucide-react";
import { Button } from "@/app/components/button";
import { getOrder } from "@/app/api/auth-client";

type OrderStatus = "loading" | "pending_payment" | "paid" | "swap_first_paid" | "error";

interface OrderConfirmationPageProps {
  orderNumber: string;
  onBackToHome: () => void;
  estimatedDelivery: string;
}

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 60; // ~2.5 min

function safeStr(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number") return String(x);
  return "";
}

export function OrderConfirmationPage({
  orderNumber,
  onBackToHome,
  estimatedDelivery,
}: OrderConfirmationPageProps) {
  const safeOrderNumber = safeStr(orderNumber);
  const safeEstimatedDelivery = safeStr(estimatedDelivery);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("loading");

  useEffect(() => {
    if (!safeOrderNumber) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let attempts = 0;
    const poll = async (): Promise<boolean> => {
      if (cancelled) return true;
      try {
        const order = await getOrder(safeOrderNumber);
        if (cancelled) return true;
        const status = order?.status as string | undefined;
        const dealType = (order as { deal_type?: string })?.deal_type;
        const hasPaymentIntentId = !!(order as { payment_intent_id?: string })?.payment_intent_id;
        // Backend sets shipping_pending when payment is confirmed; treat paid/shipping_pending as confirmed.
        if (status === "paid" || status === "shipping_pending") {
          setOrderStatus("paid");
          return true;
        }
        // Swap first payment: order still pending_payment but we have payment_intent_id (webhook ran). Show success and "we'll notify the other party".
        if (status === "pending_payment" && dealType === "swap" && hasPaymentIntentId) {
          setOrderStatus("swap_first_paid");
          return true;
        }
        setOrderStatus("pending_payment");
      } catch {
        // Keep polling on transient errors
      }
      return false;
    };
    void (async () => {
      const done = await poll();
      if (done || cancelled) return;
      intervalId = setInterval(async () => {
        attempts += 1;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setOrderStatus("error");
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
          return;
        }
        const done = await poll();
        if (done) {
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
        }
      }, POLL_INTERVAL_MS);
    })();
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [safeOrderNumber]);

  const isPaid = orderStatus === "paid";
  const isSwapFirstPaid = orderStatus === "swap_first_paid";
  const isPending = orderStatus === "loading" || orderStatus === "pending_payment";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary border-b border-primary">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-xl text-white text-center">
            {isPaid ? "Pedido Confirmado!" : isSwapFirstPaid ? "Pagamento recebido!" : "Confirmando pagamento..."}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {orderStatus === "error" && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Não foi possível confirmar o status do pagamento. Acompanhe em Minhas Compras.
            </p>
            <Button onClick={onBackToHome} size="lg">
              <Home className="w-5 h-5 mr-2" />
              Voltar para Início
            </Button>
          </div>
        )}

        {isPending && (
          <div className="flex flex-col items-center text-center py-12">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Aguardando confirmação do pagamento</h2>
            <p className="text-muted-foreground mb-6">
              Seu pedido <strong>#{safeOrderNumber}</strong> está sendo processado.
            </p>
          </div>
        )}

        {isSwapFirstPaid && (
          <div className="flex flex-col items-center text-center py-12">
            <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Pagamento recebido!</h2>
            <p className="text-muted-foreground mb-4">
              Seu pagamento foi processado com sucesso.
            </p>
            <p className="text-muted-foreground mb-6">
              A outra parte será notificada para concluir o pagamento do frete. Quando ambos tiverem pago, o pedido será confirmado e as etiquetas de envio serão geradas.
            </p>
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm mb-6">
              <p className="text-sm text-muted-foreground mb-1">Número do Pedido</p>
              <p className="text-2xl font-bold text-primary">#{safeOrderNumber}</p>
            </div>
            <Button onClick={onBackToHome} className="w-full max-w-sm" size="lg">
              <Home className="w-5 h-5 mr-2" />
              Voltar para Início
            </Button>
          </div>
        )}

        {isPaid && (
          <>
        {/* Success Icon */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          
          <h2 className="text-2xl font-bold mb-2">Pedido Realizado!</h2>
          <p className="text-muted-foreground mb-6">
            Seu pedido foi confirmado com sucesso
          </p>

          {/* Order Number */}
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm mb-6">
            <p className="text-sm text-muted-foreground mb-1">Número do Pedido</p>
            <p className="text-2xl font-bold text-primary">#{safeOrderNumber}</p>
          </div>

          {/* Delivery Info */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 w-full max-w-sm mb-8">
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-6 h-6 text-primary" />
              <h3 className="font-semibold">Previsão de Entrega</h3>
            </div>
            <p className="text-lg font-semibold text-primary">
              {safeEstimatedDelivery}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Você receberá atualizações sobre seu pedido via notificações
            </p>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h3 className="font-semibold mb-4">Próximos Passos</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-primary">1</span>
              </div>
              <div>
                <p className="font-medium">Pagamento Confirmado</p>
                <p className="text-sm text-muted-foreground">
                  Seu pagamento foi processado com sucesso
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-primary">2</span>
              </div>
              <div>
                <p className="font-medium">Preparando seu Pedido</p>
                <p className="text-sm text-muted-foreground">
                  O vendedor está separando seus produtos
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold">3</span>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Em Trânsito</p>
                <p className="text-sm text-muted-foreground">
                  Você será notificado quando o pedido for enviado
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold">4</span>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Entrega</p>
                <p className="text-sm text-muted-foreground">
                  Receba seu pedido no endereço cadastrado
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={onBackToHome}
            className="w-full"
            size="lg"
          >
            <Home className="w-5 h-5 mr-2" />
            Voltar para Início
          </Button>
          
          <p className="text-center text-sm text-muted-foreground">
            Acompanhe seu pedido em "Minhas Compras"
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
